import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useSelectionData, resolveMetricValue } from '../../services/useSelectionData';
import { getMetrics, getMetricLabel, getMetricMobileLabel, getGroup, getCategoryHeading } from '../../services/metricRegistry';
const SimpleKPI = ({ dashboardState, onMetricSelect }) => {
    var _a, _b, _c, _d;
    const [selectedMetricId, setSelectedMetricId] = useState(null);
    // County/state rows for the current selection — shared with the narrative panel
    const { countyData, stateData, isLoading } = useSelectionData(dashboardState);
    // Available metric descriptors for the current category/geo level
    const metrics = dashboardState.selectedCategoryId
        ? getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel)
        : [];
    const metricKey = metrics.map(m => m.id).join(',');
    // Sync with parent dashboardState.selectedMetricId
    useEffect(() => {
        if (dashboardState.selectedMetricId && metrics.some(m => m.id === dashboardState.selectedMetricId)) {
            setSelectedMetricId(dashboardState.selectedMetricId);
        }
    }, [dashboardState.selectedMetricId, metricKey]);
    // Auto-select first metric when the available metrics change
    useEffect(() => {
        if (metrics.length > 0) {
            if (!selectedMetricId || !metrics.some(m => m.id === selectedMetricId)) {
                const firstMetric = metrics[0].id;
                setSelectedMetricId(firstMetric);
                onMetricSelect(firstMetric);
            }
        }
        else {
            setSelectedMetricId(null);
        }
    }, [metricKey]);
    const handleCardClick = (metricId) => {
        setSelectedMetricId(metricId);
        onMetricSelect(metricId);
    };
    // Read + format a metric's value for the current geo selection. The source
    // selection (county row vs. state fallback) and the Year 2 formatting rules
    // ($/% affixes, shortage level labels, Age decimals, suppression sentinels)
    // live in resolveMetricValue, shared with the narrative panel.
    const displayValue = (metric) => {
        if (!dashboardState.selectedCategoryId) {
            return 'N/A';
        }
        if (isLoading) {
            return '...';
        }
        return resolveMetricValue(dashboardState, countyData, stateData, metric.id).formatted;
    };
    // If no metrics available, show empty state (same messages as before)
    if (metrics.length === 0) {
        return (_jsx("div", { className: "mapdashboard-scope mapdashboard-kpi-map-head", id: "kpi-header", children: _jsx("div", { className: "v5-kpi-slot", "aria-live": "polite", "aria-label": "Key performance indicators", children: _jsx("div", { className: "text-muted", children: dashboardState.selectedCategoryId ?
                        'No metrics available for this selection' :
                        'Select a category to view metrics' }) }) }));
    }
    // Guard against the brief window before the auto-select effect runs
    const activeMetric = (_a = metrics.find(m => m.id === selectedMetricId)) !== null && _a !== void 0 ? _a : metrics[0];
    // Header copy. The title is the category's heading form — the same text the
    // map card and the print header show — and the sub-line pairs the Geography
    // dropdown's own value with the Group label, so it reads back the two filters
    // that decided which metric buttons are on screen.
    const headingText = getCategoryHeading(dashboardState.selectedCategoryId);
    const geoOptionLabel = dashboardState.geoOption === 'county'
        ? 'County'
        : dashboardState.geoOption === 'state' ? 'State' : 'Nation';
    const groupLabel = (_c = (_b = getGroup(dashboardState.selectedGroupId)) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : '';
    // Which place the solo value belongs to: the drilled-into county, else the
    // selected state, else the national row.
    const NATIONAL_LOCATION = 'Nation';
    const locationName = (_d = dashboardState.selectedCounty) !== null && _d !== void 0 ? _d : (dashboardState.selectedState || NATIONAL_LOCATION);
    // Title suffix: "(Texas - TX)", or "(Harris County - TX)" once drilled into a
    // county. Nothing at the national selection, which carries no abbreviation.
    // stateCode's case varies by source — the map supplies 'tx', the search box
    // 'TX' — so normalize it, and drop the dash entirely if it is missing rather
    // than render a dangling separator.
    const stateAbbreviation = dashboardState.stateCode.toUpperCase();
    let locationSuffix = "";
    if (dashboardState.geoLevel === 'county') {
        locationSuffix = locationName === NATIONAL_LOCATION
            ? ''
            : stateAbbreviation
                ? `(${locationName}, ${stateAbbreviation})`
                : `(${locationName})`;
    }
    else {
        locationSuffix = locationName === NATIONAL_LOCATION
            ? ''
            : stateAbbreviation
                ? `(${locationName} - ${stateAbbreviation})`
                : `(${locationName})`;
    }
    return (_jsx("div", { className: "mapdashboard-scope mapdashboard-kpi-map-head d-print-none", id: "kpi-header", children: _jsx("div", { className: "hv-map-head", children: _jsxs("div", { className: "hv-hh-h2", "aria-live": "polite", "aria-label": "Key performance indicators", children: [_jsxs("div", { className: "hv-hh-toprow", children: [_jsxs("div", { className: "hv-hh-titleblock", children: [_jsxs("h3", { className: "hv-hh-maintitle", children: [headingText, locationSuffix ? ` ${locationSuffix}` : ''] }), _jsxs("div", { className: "hv-hh-filtersub fw-semibold small text-gray-600", children: [geoOptionLabel, " \u00B7 ", groupLabel, " "] })] }), _jsxs("div", { className: "hv-solo-val", children: [_jsx("span", { className: "v", children: displayValue(activeMetric) }), _jsx("span", { className: "hv-hh-filtersub fw-semibold small text-gray-600 d-none d-lg-inline", children: getMetricLabel(activeMetric.id, dashboardState.geoLevel) }), _jsx("span", { className: "hv-hh-filtersub fw-semibold small text-gray-600 d-lg-none mobilemetriclabel", dangerouslySetInnerHTML: { __html: getMetricMobileLabel(activeMetric.id, dashboardState.geoLevel) } })] })] }), metrics.length > 1 && (_jsx("div", { className: "hv-hh-btns", children: metrics.map((metric) => {
                            const isSelected = activeMetric.id === metric.id;
                            const label = getMetricLabel(metric.id, dashboardState.geoLevel);
                            return (_jsxs("button", { type: "button", className: `hv-hh-card${isSelected ? ' active' : ''}`, "aria-pressed": isSelected, "aria-label": `${label}: ${displayValue(metric)}${isSelected ? ' (selected)' : ''}`, onClick: () => handleCardClick(metric.id), children: [_jsx("span", { className: "cv", children: displayValue(metric) }), _jsx("span", { className: "fw-semibold small text-wrap text-gray-600", children: label })] }, metric.id));
                        }) }))] }) }) }));
};
export default SimpleKPI;
